"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";

type Step = {
  step: string;
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  desktopImageFirst: boolean;
};

const STEPS: Step[] = [
  {
    step: "01",
    title: "Generate your pitch instantly",
    description:
      "Choose a template, tone, and length to create a strong starting point in seconds.",
    imageSrc: "/images/how-it-works/how-it-works-generate.webp",
    imageAlt: "Pitch generation screen in the PitchBuddy app",
    desktopImageFirst: true,
  },
  {
    step: "02",
    title: "Practice your way",
    description:
      "Use Scripted Practice, Skill Focus, Free Practice, or Daily Drills based on what you want to improve.",
    imageSrc: "/images/how-it-works/how-it-works-practice.webp",
    imageAlt: "Practice mode options in the PitchBuddy app",
    desktopImageFirst: false,
  },
  {
    step: "03",
    title: "Get detailed feedback",
    description:
      "Review insights across script accuracy, confidence, energy, pace, fillers, and progress over time.",
    imageSrc: "/images/how-it-works/how-it-works-feedback.webp",
    imageAlt: "Detailed speaking feedback view in the PitchBuddy app",
    desktopImageFirst: true,
  },
];

export default function HowItWorksSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const stepRefs = useRef<Array<HTMLElement | null>>([]);
  const [progress, setProgress] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>(
    STEPS.map((_, index) => index === 0)
  );

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setVisibleSteps(STEPS.map(() => true));
      setProgress(1);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        setVisibleSteps((prev) => {
          const next = [...prev];
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const index = Number(
              (entry.target as HTMLElement).getAttribute("data-step-index") ?? -1
            );
            if (index >= 0) next[index] = true;
          }
          return next;
        });
      },
      { threshold: 0.35, rootMargin: "0px 0px -8% 0px" }
    );

    stepRefs.current.forEach((node) => {
      if (node) observer.observe(node);
    });

    const updateProgress = () => {
      const section = sectionRef.current;
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const start = viewportHeight * 0.78;
      const end = rect.height - viewportHeight * 0.22;
      const value = (start - rect.top) / Math.max(end, 1);
      const clamped = Math.max(0, Math.min(1, value));
      setProgress(clamped);
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        updateProgress();
        ticking = false;
      });
    };

    updateProgress();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section
      id="how-it-works"
      ref={sectionRef}
      className="pt-2 pb-15 sm:pt-3 sm:pb-16 lg:pt-4 lg:pb-18"
    >
      <div className="mx-auto max-w-3xl text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-400">
          How it works
        </p>
        <h2 className="mt-2.5 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
          Practice your pitch in 3 simple steps
        </h2>
        <p className="mt-2.5 text-base leading-7 text-muted-foreground sm:text-lg">
          Generate, practice, and get feedback that helps you improve.
        </p>
      </div>

      <div className="relative mt-8 space-y-7 sm:mt-9 sm:space-y-8 lg:mt-11 lg:space-y-8">
        <div
          aria-hidden="true"
          className="absolute bottom-8 left-4 top-8 w-px bg-border/45 lg:left-1/2 lg:-translate-x-1/2"
        />
        <div
          aria-hidden="true"
          className="absolute left-4 top-8 w-px bg-primary/35 transition-[height] duration-200 ease-out lg:left-1/2 lg:-translate-x-1/2"
          style={{ height: `calc((100% - 4rem) * ${progress})` }}
        />
        {STEPS.map((step, index) => {
          const isLast = index === STEPS.length - 1;

          return (
            <article
              key={step.step}
              ref={(node) => {
                stepRefs.current[index] = node;
              }}
              data-step-index={index}
              className={`group relative grid grid-cols-1 items-center gap-5 pl-9 transition-all duration-500 ease-out sm:gap-6 lg:grid-cols-2 lg:gap-7 lg:pl-0 ${
                visibleSteps[index]
                  ? "translate-y-0 opacity-100"
                  : "translate-y-2 opacity-0"
              }`}
            >
              <span
                aria-hidden="true"
                className={`absolute left-4 top-4 z-10 inline-flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border bg-background text-xs font-semibold tracking-wide shadow-[0_0_0_4px_var(--background)] transition-colors duration-200 lg:left-1/2 lg:top-1/2 lg:-translate-y-1/2 ${
                  visibleSteps[index]
                    ? "border-amber-600/45 text-amber-700 dark:border-primary/45 dark:text-primary"
                    : "border-amber-600/28 text-amber-700/85 dark:border-primary/28 dark:text-primary/85"
                }`}
              >
                {step.step}
              </span>
              {!isLast ? (
                <span
                  aria-hidden="true"
                  className="absolute left-4 top-12 h-[calc(100%-2rem)] w-px -translate-x-1/2 bg-border/70 lg:hidden"
                />
              ) : null}

              <div
                className={`${
                  step.desktopImageFirst ? "lg:order-1" : "lg:order-2"
                }`}
              >
                <div className="mx-auto w-full max-w-[12.75rem] sm:max-w-[13.75rem] lg:max-w-[14.4rem]">
                  <Image
                    src={step.imageSrc}
                    alt={step.imageAlt}
                    width={900}
                    height={640}
                    sizes="(max-width: 767px) 204px, (max-width: 1023px) 220px, 230px"
                    className="h-auto w-full object-contain"
                  />
                </div>
              </div>

              <div
                className={`${
                  step.desktopImageFirst ? "lg:order-2" : "lg:order-1"
                }`}
              >
                <div
                  className={`relative mx-auto max-w-[30rem] ${
                    step.desktopImageFirst ? "lg:mr-auto" : "lg:ml-auto"
                  }`}
                >
                  <h3 className="text-2xl font-semibold tracking-tight text-foreground sm:text-[1.7rem]">
                    {step.title}
                  </h3>
                  <p className="mt-2.5 text-sm leading-6 text-muted-foreground sm:text-base">
                    {step.description}
                  </p>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
